import { useCallback, useRef, useState } from 'react';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { updateProject } from '@/lib/api';

interface Props {
  projectId: string;
  urls: string[];
  onChange: (urls: string[]) => void;
}

// Hergebruikt het bestaande foto-bucket; huisstijl-bestanden krijgen een eigen
// prefix zodat ze niet botsen met de pakket-foto's (<packageId>/...).
const BUCKET = 'gift_package_photos';

/**
 * BrandAssetUpload — huisstijl-voorbeelden (logo, merk-richtlijn, referentie)
 * per project uploaden. URLs gaan naar projects.brand_asset_urls; bestanden naar
 * bucket gift_package_photos onder <projectId>/brand/.
 */
export function BrandAssetUpload({ projectId, urls, onChange }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingUrl, setDeletingUrl] = useState<string | null>(null);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setError(null);
      setUploading(true);
      try {
        const newUrls: string[] = [];
        for (const file of Array.from(files)) {
          const ext = (file.name.split('.').pop() ?? 'png').toLowerCase();
          const safeExt = ext.length <= 5 ? ext : 'png';
          const fname = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${safeExt}`;
          const path = `${projectId}/brand/${fname}`;
          const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
            contentType: file.type || 'image/png',
            cacheControl: '3600',
            upsert: false,
          });
          if (upErr) throw upErr;
          const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
          newUrls.push(data.publicUrl);
        }
        const merged = [...urls, ...newUrls];
        await updateProject(projectId, { brand_asset_urls: merged });
        onChange(merged);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload mislukt');
      } finally {
        setUploading(false);
        if (fileInput.current) fileInput.current.value = '';
      }
    },
    [projectId, urls, onChange]
  );

  async function handleDelete(url: string) {
    setDeletingUrl(url);
    setError(null);
    try {
      const marker = `/${BUCKET}/`;
      const idx = url.indexOf(marker);
      if (idx >= 0) {
        const path = url.slice(idx + marker.length);
        await supabase.storage.from(BUCKET).remove([path]);
      }
      const updated = urls.filter((u) => u !== url);
      await updateProject(projectId, { brand_asset_urls: updated });
      onChange(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    } finally {
      setDeletingUrl(null);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-bmade-500">
        Upload het logo of een huisstijl-voorbeeld van de klant (afbeelding of foto). Dit blijft per
        project bewaard als referentie bij het samenstellen.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          Huisstijl uploaden
        </button>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {urls.length === 0 ? (
        <p className="text-xs text-bmade-500">Nog geen huisstijl-voorbeeld geüpload.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {urls.map((url) => (
            <div
              key={url}
              className="group relative overflow-hidden rounded-lg border border-bmade-200 bg-bmade-50 dark:border-bmade-800 dark:bg-bmade-900"
            >
              <img
                src={url}
                alt="Huisstijl voorbeeld"
                className="aspect-square w-full object-contain p-2"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => void handleDelete(url)}
                disabled={deletingUrl === url}
                className="absolute right-1 top-1 inline-flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100 disabled:opacity-100"
                title="Verwijder huisstijl-voorbeeld"
              >
                {deletingUrl === url ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
                Verwijderen
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
